import React from 'react';
import { Box, Typography, Tooltip, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LenderRequirementsDisplaySection } from './LenderRequirementsDisplaySection';
import { LenderRequirementsGatherSection } from './LenderRequirementsGatherSection';
import { CreditSubmission, FinanceProgram, InsuranceQuote } from '@trnsact/trnsact-shared-types';
import { Nullable } from 'global';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface Props {
  onEnterRequirements?: () => void;
  onSelectSubmission?: () => void;
  onSetupFinanceProgram?: () => void;
  selectedSubmission: Nullable<CreditSubmission>;
  selectedFinanceProgram: Nullable<FinanceProgram>;
  insuranceQuote: Nullable<InsuranceQuote>;
}

export const LenderRequirementsSection = ({
  onEnterRequirements,
  onSelectSubmission,
  onSetupFinanceProgram,
  selectedSubmission,
  selectedFinanceProgram,
  insuranceQuote,
}: Props) => {
  const classes = useStyles();

  return (
    <Box>
      <Box className={classes.headerRow}>
        <Typography className={classes.sectionTitle}>Certificate of Insurance (COI) Acquisition</Typography>

        <Box className={classes.rightTools}>
          <Box className={classes.saveTimeWrap}>
            <Typography className={classes.saveTimeText}>Save time on future deals</Typography>
            <InfoOutlinedIcon className={classes.infoIcon} />
          </Box>

          <Tooltip
            title="Set up finance programs to apply insurance requirements automatically in the future"
            placement="bottom"
            arrow
          >
            <Button
              color="primary"
              size="small"
              className={classes.linkBtn}
              endIcon={<OpenInNewIcon fontSize="small" />}
              onClick={() => (onSetupFinanceProgram ? onSetupFinanceProgram() : undefined)}
            >
              SETUP FINANCE PROGRAM
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {(selectedSubmission && selectedFinanceProgram) || insuranceQuote?.insuranceCoverage ? (
        <LenderRequirementsDisplaySection
          selectedFinanceProgram={selectedFinanceProgram}
          selectedSubmission={selectedSubmission}
          insuranceQuote={insuranceQuote}
          onEnterRequirements={onEnterRequirements}
        />
      ) : (
        <LenderRequirementsGatherSection
          onEnterRequirements={onEnterRequirements}
          onSelectSubmission={onSelectSubmission}
          onSetupFinanceProgram={onSetupFinanceProgram}
        />
      )}
    </Box>
  );
};

const useStyles = makeStyles({
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    flexWrap: 'wrap',
    marginBottom: '10px',
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '14px',
  },
  sectionSubtitle: {
    marginTop: '6px',
    fontSize: '12px',
    color: '#4b5563',
  },
  rightTools: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  saveTimeWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '2px',
  },
  saveTimeText: {
    fontSize: '12px',
    color: '#6b7280',
  },
  infoIcon: {
    fontSize: '16px',
    color: '#2563eb',
  },
  linkBtn: {
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '12px',
    paddingLeft: '8px',
    paddingRight: '8px',
  },
});
